import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { NumberParam, StringParam, useQueryParam } from "use-query-params";
import { StartPage } from "../../../../components";
import ModalVariables from "../../../../components/modal-alerts";
import { useUi } from "../../../../context/ui.context";
import { kubernetesService } from "../../../../api";
import { icons } from "../../../../icons";
import { extractErrorMessage } from "../../../../utils/error-extractor";
import { formatDate } from "../../../../utils/date-formatter";
import { StaffCluster } from "../types";

const ClustersList: FC = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { viewAlert } = useUi();

  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [clusters, setClusters] = useState<StaffCluster[]>([]);
  const [paginationData, setPaginationData] = useState<Record<string, unknown> | null>(null);

  const [viewModal, setViewModal] = useState(false);
  const [activeAnimation, setActiveAnimation] = useState(false);
  const [modalType, setModalType] = useState<any>('');
  const [dataToEdit, setDataToEdit] = useState<any>(null);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertSubTitle, setAlertSubTitle] = useState<any>('');

  const [page] = useQueryParam("page", NumberParam);
  const [ordering] = useQueryParam("ordering", StringParam);
  const [pageSize] = useQueryParam("page_size", NumberParam);
  const [searchString] = useQueryParam("search", StringParam);

  const updateList = useCallback(() => {
    setPageLoading(true);
    const params: Record<string, unknown> = {
      page: page || 1,
      page_size: pageSize || 10,
      search: searchString || "",
    };
    if (ordering) {
      params.ordering = ordering;
    }
    kubernetesService
      .getList(params as any)
      .then((res) => {
        const data = res.data;
        const objects = (data?.objects || []) as StaffCluster[];
        setClusters(objects);
        setPaginationData(data);
      })
      .catch((error) => {
        const message =
          error?.response?.data?.detail ||
          extractErrorMessage(error?.response?.data) ||
          t("FAILED_TO_LOAD_CLUSTERS");
        viewAlert({
          severity: "error",
          message,
        });
      })
      .finally(() => {
        setPageLoading(false);
      });
  }, [page, pageSize, ordering, searchString, t, viewAlert]);

  useEffect(() => {
    updateList();
  }, [updateList]);

  const performDelete = useCallback(
    (cluster: StaffCluster) => {
      setPageLoading(true);
      kubernetesService
        .deleteCluster(cluster.id)
        .then(() => {
          viewAlert({
            severity: "information",
            message: t("CLUSTER_DELETED"),
          });
          updateList();
        })
        .catch((error) => {
          const message =
            error?.response?.data?.detail ||
            extractErrorMessage(error?.response?.data) ||
            t("FAILED_TO_DELETE_CLUSTER");
          viewAlert({
            severity: "error",
            message,
          });
        })
        .finally(() => {
          setPageLoading(false);
        });
    },
    [t, updateList, viewAlert]
  );

  const closeModal = (result?: any) => {
    if (modalType === 'universal' && dataToEdit) {
      performDelete(dataToEdit);
    }
    setViewModal(false);
    setActiveAnimation(false);
    setModalType('');
    setDataToEdit(null);
    setAlertSubTitle('');
  };

  const closeModalCancel = () => {
    setViewModal(false);
    setActiveAnimation(false);
    setModalType('');
    setDataToEdit(null);
    setAlertSubTitle('');
  };

  const handleDelete = (cluster: StaffCluster) => {
    setAlertSubTitle(t("CONFIRM_DELETE_CLUSTER"));
    setModalType('universal');
    setAlertTitle(t("DELETE"));
    setDataToEdit(cluster);
    setViewModal(true);
    setActiveAnimation(true);
  };

  const columns = useMemo(
    () => [
      { title: t("NAME"), key: "name", sorted: true },
      { title: t("VERSION"), key: "version", sorted: true },
      { title: t("IMAGE"), key: "image" },
      { title: t("STATUS"), key: "status", sorted: true },
      { title: t("PROJECT_ID"), key: "project_id" },
      { title: t("CREATED_AT"), key: "created_at_display", sorted: true },
      { title: "", key: "props" },
    ],
    [t]
  );

  const items = useMemo(
    () => [
      {
        title: t("DELETE"),
        id: "delete",
        icon: icons.DeleteIcon,
        divider: false,
        onClick: (cluster: StaffCluster) => {
          handleDelete(cluster);
        },
      },
    ],
    [t]
  );

  const tableRows = useMemo(
    () =>
      clusters.map((cluster) => ({
        ...cluster,
        created_at_display: formatDate(cluster.created_at),
        status: cluster.status?.toUpperCase() || "",
        navigate: `/openstack/clusters/${cluster.id}/overview`,
      })),
    [clusters]
  );

  return (
    <>
      <StartPage
        title={t("CLUSTERS")}
        noResourceText={t("NO_CLUSTERS")}
        haveResource={clusters.length > 0}
        pageLoading={pageLoading}
        withoutCreateResourceButton
        tableData={{
          columns: columns,
          data: tableRows,
          hideCheckbox: true,
          props: items,
          paginationData: paginationData,
          updateData: updateList,
        }}
      />
      <ModalVariables
        modalType={modalType}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={closeModal}
        closeModalCancel={closeModalCancel}
        alertTitle={alertTitle}
        alertSubTitle={alertSubTitle}
        dataToEdit={dataToEdit}
      />
    </>
  );
};

export default ClustersList;
